import React, { useEffect, useState } from "react";
import Layout from "../../Component/Layout";
import axios from "../../http"; // axios instance that attaches the JWT (the API now enforces RBAC)
import Select from "react-select";
import MaskedValue from "../../Component/MaskedValue";
import { MASTER_API } from "../../apiConfig";
import { useNavigate, useLocation } from "react-router-dom";


// Formats any date/date-time value as DD/MM/YYYY (or DD/MM/YYYY HH:mm for timestamps)
const formatDateDMY = (value, withTime = false) => {
  if (!value) return "-";
  const d = new Date(value);
  if (isNaN(d.getTime())) return "-";
  const dd = String(d.getDate()).padStart(2, "0");
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const yyyy = d.getFullYear();
  const datePart = `${dd}/${mm}/${yyyy}`;
  if (!withTime) return datePart;
  const hh = String(d.getHours()).padStart(2, "0");
  const min = String(d.getMinutes()).padStart(2, "0");
  return `${datePart} ${hh}:${min}`;
};

const compactInput = {
  width: "100%",
  fontSize: "13px",
  padding: "4px 8px",
  height: "32px",
  boxSizing: "border-box",
  border: "1px solid #dee2e6",
  borderRadius: "4px",
};

const compactLabel = {
  display: "block",
  fontSize: "12px",
  fontWeight: 600,
  marginBottom: 2,
  color: "#334155"
};

// Matches Product Master's customSelectStyles exactly (react-select)
const customSelectStyles = {
  control: (base) => ({
    ...base,
    minHeight: 32,
    height: 32,
    fontSize: "13px",
    borderColor: "#dee2e6",
    boxShadow: "none",
    "&:hover": { borderColor: "#b3b3b3" },
  }),
  valueContainer: (base) => ({ ...base, padding: "0 8px", height: "32px", display: "flex", alignItems: "center" }),
  input: (base) => ({ ...base, margin: 0, padding: 0 }),
  dropdownIndicator: (base) => ({ ...base, padding: 4 }),
  menu: (base) => ({ ...base, fontSize: "13px", zIndex: 9999 }),
  menuPortal: (base) => ({ ...base, zIndex: 9999 }),
  option: (base, state) => ({
    ...base,
    backgroundColor: state.isSelected ? "#1e293b" : state.isFocused ? "#f1f5f9" : "white",
    color: state.isSelected ? "white" : "#334155",
    cursor: "pointer",
    fontSize: "13px",
  }),
};

const thStyle = {
  background: "#f8fafc",
  fontSize: 12,
  fontWeight: 600,
  padding: "10px",
  borderBottom: "1px solid #e5e7eb",
  color: "#1e293b",
  textAlign: "left",
  whiteSpace: "normal",
  wordBreak: "break-word",
};

const tdStyle = {
  fontSize: 13,
  padding: "10px",
  borderBottom: "1px solid #f1f5f9",
  color: "#334155",
  whiteSpace: "normal",
  wordBreak: "break-word",
};

const gridTwoCols = {
  display: "grid",
  gridTemplateColumns: "1fr 1fr",
  gap: 12,
};

// Matches Product Master's `styles` table constants exactly
const styles = {
  erpTable: { width: "100%", borderCollapse: "collapse" },
  erpTh: { background: "#f8fafc", fontSize: 12, fontWeight: 600, padding: "10px", borderBottom: "1px solid #e5e7eb", color: "#1e293b" },
  erpTd: { fontSize: 13, padding: "10px", borderBottom: "1px solid #f1f5f9", color: "#334155" },
};

// eslint-disable-next-line react/prop-types
const TabButton = ({ index, activeTab, setActiveTab, label }) => (
  <button
    type="button"
    className={`btn btn-sm ${activeTab === index ? "btn-dark" : "btn-light"}`}
    onClick={() => setActiveTab(index)}
    style={{
      fontSize: "13px",
      padding: "5px 12px",
      border: "1px solid #eee",
    }}
    aria-pressed={activeTab === index}
  >
    {label}
  </button>
);

const initialFormState = {
  workcenter_name: "",
  workcenter_description: "",

  // Basic Data
  workcenter_category: "",
  person_responsible: "",


  // Capacity
  capacity_category: "", capacity_responsible: "", factory_calendar: "", capacity_base_unit: "",
  start_time: "", end_time: "", break_time: "", capacity_utilization: "", no_of_capacity: "", capacity_by_length: "", relevant_for_scheduling: "no",

  // Costing & Activities (Flat)
  start_date: "", end_date: "", controlling_area: "", cost_center: "",


  // Breakdown
  breakdown_start_date: "", breakdown_end_date: "", breakdown_start_time: "", breakdown_end_time: "", no_of_machines_breakdown: "",
};

/* ======================================================================
   RBAC  (module id MD_WORKCENTER — must match TILE_REGISTRY / FIELD_REGISTRY)
   Access types (from Create Role):
     CREATE  -> can add work centers, upload, manage and publish drafts
                (CREATE field list = fields turned off on the form)
     DISPLAY -> can view work centers
                (DISPLAY field list = values that are masked)
     CHANGE  -> can view + edit/delete
                (CHANGE field list = fields that are read-only)
   Admin / Full Access users bypass every check.
   Fails closed: until permissions load nobody gets access.
   The backend enforces the same rules; this only drives the UI.
   ====================================================================== */
const useWorkcenterAccess = () => {
  const [state, setState] = useState({
    loading: true, error: null, isAdmin: false, types: [],
    off: { DISPLAY: [], CHANGE: [], CREATE: [] } // type -> field ids turned off
  });

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(`${MASTER_API}/workcenter/my-access`);
        const a = res.data || {};
        if (!cancelled) {
          setState({
            loading: false,
            error: null,
            isAdmin: !!a.admin,
            types: (a.types || []).map((x) => String(x).toUpperCase()),
            off: {
              DISPLAY: a.off?.DISPLAY || [],
              CHANGE: a.off?.CHANGE || [],
              CREATE: a.off?.CREATE || []
            }
          });
        }
      } catch (e) {
        console.error("[Workcenter RBAC] could not load /workcenter/my-access", e);
        const why = e.response
          ? `${e.response.status} ${e.response.data?.message || e.response.statusText || ""}`
          : (e.message || "network error");
        if (!cancelled) setState((st) => ({ ...st, loading: false, isAdmin: false, error: why }));
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const has = (...t) => state.isAdmin || t.some((x) => state.types.includes(x));

  // state of one field for the current form mode
  //  create             -> hidden when the CREATE list turns it off
  //  view / view_draft  -> masked when the DISPLAY list turns it off
  //  edit               -> masked if DISPLAY hides it, read-only if CHANGE turns it off
  const fieldState = (field, mode) => {
    if (state.isAdmin) return { hidden: false, masked: false, readOnly: false };
    const off = (type) => state.off[type].includes(field);
    if (mode === "create") return { hidden: off("CREATE"), masked: false, readOnly: false };
    if (mode === "view" || mode === "view_draft") return { hidden: false, masked: off("DISPLAY"), readOnly: false };
    if (mode === "edit") return { hidden: false, masked: off("DISPLAY"), readOnly: off("CHANGE") };
    return { hidden: false, masked: false, readOnly: false };
  };

  return {
    loading: state.loading,
    error: state.error,
    isAdmin: state.isAdmin,
    canView: has("DISPLAY", "CHANGE", "REVIEW", "CONVERT"),
    canCreate: has("CREATE"),
    canEdit: has("CHANGE"),
    fieldState
  };
};

// One labelled field: hidden / masked / read-only according to its state.
const FieldBox = ({ st, label, children }) => {
  if (st.hidden) return null;
  return (
    <div>
      <label style={compactLabel}>{label}</label>
      {st.masked
        ? <MaskedValue />
        : <div style={st.readOnly ? { pointerEvents: "none", opacity: 0.65 } : undefined}>{children}</div>}
    </div>
  );
};

// A whole section (a table): hidden / masked / read-only.
const TableGuard = ({ st, style, children }) => {
  if (st.hidden) return null;
  if (st.masked) return <div style={style}><MaskedValue /></div>;
  return <div style={st.readOnly ? { ...style, pointerEvents: "none", opacity: 0.65 } : style}>{children}</div>;
};

const Workcenter = () => {

  const access = useWorkcenterAccess();
  const canCreate = access.canCreate;
  const canEdit = access.canEdit;
  const [locations, setLocations] = useState([]);
  const [selectedLocationId, setSelectedLocationId] = useState("");
  const [selectedLocationName, setSelectedLocationName] = useState("");
  const [showModal, setShowModal] = useState(false);
  const [activeTab, setActiveTab] = useState(0);
  const [workcenterLogs, setWorkcenterLogs] = useState([]);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [workcenters, setWorkcenters] = useState([]);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(initialFormState);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [showDraftModal, setShowDraftModal] = useState(false);
  const [draftsList, setDraftsList] = useState([]);
  const [isPublishingAll, setIsPublishingAll] = useState(false);
  const [publishingId, setPublishingId] = useState(null);
  const [isDraftView, setIsDraftView] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);


  // ---- Machine Modal State ----
  const [machineRows, setMachineRows] = useState([]);
  const [machineOptions, setMachineOptions] = useState([]);
  const navigate = useNavigate();
  const location = useLocation();



  const [classificationRows, setClassificationRows] = useState([
    {
      workcenterclassification: "",
      workcenterclassificationuom: "",
    },
  ]);

  const [activityRows, setActivityRows] = useState([
    {
      workcenteractivity: "setup",
      workcenteractivityuom: "",
      is_selected: null,
    },
    {
      workcenteractivity: "machine",
      workcenteractivityuom: "",
      is_selected: null,
    },
    {
      workcenteractivity: "labour",
      workcenteractivityuom: "",
      is_selected: null,
    },
    {
      workcenteractivity: "overhead",
      workcenteractivityuom: "",
      is_selected: null,
    },
  ]);
  const [uomList, setUomList] = useState([]);
  const [personResponsibleList, setPersonResponsibleList] = useState([]);
  const [workcenterConfigList, setWorkcenterConfigList] = useState([]);
  const [machineConfigData, setMachineConfigData] = useState([]);
  const [workcenterConfigOptions, setWorkcenterConfigOptions] = useState([]);
  const [capacityResponsibleList, setCapacityResponsibleList] = useState([]);
  const [costCenterList, setCostCenterList] = useState([]);
  const [controllingAreaList, setControllingAreaList] = useState([]);
  const [workcenterCategoryList, setWorkcenterCategoryList] = useState([]);
  const [capacityCategoryList, setCapacityCategoryList] = useState([]);
  const [factoryCalendarList, setFactoryCalendarList] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [searchWorkcenter, setSearchWorkcenter] = useState("");
  const [searchDescription, setSearchDescription] = useState("");
  const itemsPerPage = 10;
  useEffect(() => {
    loadPlants();
    loadWorkcenters();
    loadDrafts();
    loadMachineOptions();
    loadWorkcenterConfigOptions();
    loadUom();
    loadPersonResponsible();
    loadWorkcenterConfig();
    loadCapacityResponsible();
    loadCostCenter();
    loadControllingArea();
    loadWorkcenterCategory();
    loadCapacityCategory();
    loadFactoryCalendar();
  }, []);

  const loadDrafts = async () => {
    try {
      const res = await axios.get(`${MASTER_API}/workcenter/get-drafts`);
      if (res.data.success) setDraftsList(res.data.drafts || []);
    } catch (err) { console.error("Failed to load drafts", err); }
  };
  const loadMachineOptions = async () => {
    try {
      const res = await axios.get(
        `${MASTER_API}/configuration/machine-config`
      );

      const data = res.data || [];

      // ✅ keep full records around so the description can be looked up later
      setMachineConfigData(data);

      const options = data.map((m) => ({
        value: m.machine_name,
        label: m.machine_name,
      }));

      setMachineOptions(options);

    } catch (err) {
      console.error("Failed to load machines", err);
      setMachineOptions([]);
      setMachineConfigData([]);
    }
  };

  // ✅ Header "Workcenter Name" / "Workcenter Description" fields must
  // pull from the actual Workcenter Config table (not Machine Config)
  const loadWorkcenterConfigOptions = async () => {
    try {
      const res = await axios.get(
        `${MASTER_API}/configuration/workcenter-config`
      );

      setWorkcenterConfigOptions(res.data || []);

    } catch (err) {
      console.error("Failed to load workcenter config", err);
      setWorkcenterConfigOptions([]);
    }
  };

  const buildEmptyMachineRows = (count) => {
    return Array.from({ length: count }, () => ({
      machine_name: "",
      workcenter_description: "",
      person_responsible: "",
      workcenter_location: "",
      start_time: "",
      end_time: "",
      break_mins: "",
      capacity_utilization: "",
      capacity_by_length: "",
      available_capacity: "",
    }));
  };

  useEffect(() => {
    const queryParams = new URLSearchParams(location.search);
    const viewWorkcenterName = queryParams.get("view");

    // We wait until the workcenters list is fetched from the API
    if (viewWorkcenterName && workcenters.length > 0 && !showModal) {
      const wcToView = workcenters.find(
        (wc) => wc.workcenter_name === viewWorkcenterName || wc.workcenter_description === viewWorkcenterName
      );
      if (wcToView) {
        openEditModal(wcToView);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search, workcenters]);

  const calcMachineAvailableCapacity = (row) => {
    const parseTime = (t) => {
      if (!t) return null;
      const m = t.match(/^(\d{1,2}):(\d{2})$/);
      if (m) return Number(m[1]) * 60 + Number(m[2]);
      return null;
    };
    const startM = parseTime(row.start_time);
    const endM = parseTime(row.end_time);
    if (startM === null || endM === null) return "";
    let diff = endM - startM;
    if (diff <= 0) diff += 24 * 60;
    const breakT = Number(row.break_mins || 0);
    const eff = Math.max(0, diff - breakT);
    const util = Number(row.capacity_utilization || 0) / 100;
    const avMins = eff * util;
    if (isNaN(avMins) || !isFinite(avMins)) return "";
    return (avMins / 60).toFixed(2);
  };

  const updateMachineRow = (index, patch) => {
    setMachineRows(prev => {
      const updated = prev.map((r, i) => i === index ? { ...r, ...patch } : r);
      // Recalculate available_capacity for changed row
      const row = { ...updated[index], ...patch };
      const avCap = calcMachineAvailableCapacity(row);
      updated[index] = { ...updated[index], ...patch, available_capacity: avCap };
      return updated;
    });
  };

  // Load existing machine rows from DB once when entering edit mode
  useEffect(() => {
    if (!editingId) return;
    (async () => {
      try {
        const res = await axios.get(`${MASTER_API}/workcenter/machines/${editingId}`);
        const data = res.data;
        if (data.success && data.data.length > 0) {
          const loaded = data.data.map(r => ({
            machine_name: r.machine_name || "",
            workcenter_description: r.workcenter_description || "",
            person_responsible: r.person_responsible || "",
            workcenter_location: r.workcenter_location || "",
            start_time: (r.start_time || "").slice(0, 5),
            end_time: (r.end_time || "").slice(0, 5),
            break_mins: r.break_time !== null && r.break_time !== undefined ? String(r.break_time) : "",
            capacity_utilization: r.capacity_utilization !== null ? String(r.capacity_utilization) : "",
            capacity_by_length: r.capacity_by_length !== null ? String(r.capacity_by_length) : "",
            available_capacity: r.available_capacity !== null ? String(r.available_capacity) : "",
            breakdown_start_date: r.breakdown_start_date ? r.breakdown_start_date.split("T")[0] : "",
            breakdown_end_date: r.breakdown_end_date ? r.breakdown_end_date.split("T")[0] : "",
            breakdown_start_time: (r.breakdown_start_time || "").slice(0, 5),
            breakdown_end_time: (r.breakdown_end_time || "").slice(0, 5),
          }));
          setMachineRows(loaded);
        }
      } catch (err) {
        // ignore - rows will be built by the count-sync effect below
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editingId]);

  // Keep the machine rows list in sync with No. of Capacity as the user types it
  useEffect(() => {
    const count = Number(form.no_of_capacity || 0);
    setMachineRows(prev => {
      if (count < 1) return [];
      const base = prev || [];
      return Array.from({ length: count }, (_, i) => base[i] || {
        machine_name: "", workcenter_description: "", person_responsible: "",
        workcenter_location: "", start_time: "", end_time: "", break_mins: "",
        capacity_utilization: "", capacity_by_length: "", available_capacity: "",
        breakdown_start_date: "", breakdown_end_date: "", breakdown_start_time: "",
        breakdown_end_time: "",
      });
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.no_of_capacity]);

  // Auto-sum machine rows into the workcenter's Available Capacity / Capacity By Length
  useEffect(() => {
    if (machineRows.length === 0) return;
    let totalAvail = 0;
    let totalLength = 0;
    machineRows.forEach(r => {
      const av = parseFloat(r.available_capacity);
      const cl = parseFloat(r.capacity_by_length);
      if (!isNaN(av)) totalAvail += av;
      if (!isNaN(cl)) totalLength += cl;
    });
    updateForm({
      available_capacity: totalAvail > 0 ? totalAvail.toFixed(2) : "",
      capacity_by_length: totalLength > 0 ? totalLength : "",
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [machineRows]);

  // const saveMachinesToDB = async (workcenter_id) => {
  //   if (!machineRows || machineRows.length === 0) return;
  //   try {
  //     await fetch(`${MASTER_API}/workcenter/machines/save`, {
  //       method: "POST",
  //       headers: { "Content-Type": "application/json" },
  //       body: JSON.stringify({
  //         workcenter_id,
  //         workcenter_name: form.workcenter_name,
  //         machines: machineRows,
  //       }),
  //     });
  //   } catch (err) {
  //     console.error("Failed to save machines:", err);
  //   }
  // };

  const handleBulkExcelUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const uploadData = new FormData();
    uploadData.append("file", file);

    try {
      const res = await axios.post(`${MASTER_API}/workcenter/parse-excel`, uploadData, {
        headers: { "Content-Type": "multipart/form-data" }
      });
      if (res.data.success) {
        setMessage({ type: "success", text: res.data.message });
        loadDrafts();
        setShowUploadModal(false);
        setShowDraftModal(true);
      } else {
        setMessage({ type: "error", text: res.data.message });
      }
    } catch (err) {
      setMessage({ type: "error", text: err.response?.data?.error || "Failed to upload Excel file" });
    } finally {
      e.target.value = null;
    }
  };

  const handlePublishDraft = async (workcenter_name) => {
    setPublishingId(workcenter_name);
    try {
      const res = await axios.post(`${MASTER_API}/workcenter/publish-draft`, { workcenter_name });
      if (res.data.success) {
        setMessage({ type: "success", text: res.data.message });
        loadDrafts();
        loadWorkcenters();
      }
    } catch (err) { setMessage({ type: "error", text: "Failed to publish draft" }); }
    finally { setPublishingId(null); }
  };

  const handlePublishAllDrafts = async () => {
    if (draftsList.length === 0) return;
    setIsPublishingAll(true);
    try {
      const res = await axios.post(`${MASTER_API}/workcenter/publish-all-drafts`);
      if (res.data.success) {
        setMessage({ type: "success", text: res.data.message });
        loadDrafts();
        loadWorkcenters();
        setShowDraftModal(false);
      }
    } catch (error) { setMessage({ type: "error", text: error.response?.data?.message || "Error saving drafts" }); }
    finally { setIsPublishingAll(false); }
  };

  const openDraftView = (wc) => {

    // =====================================
    // LOCATION MATCH
    // =====================================

    const matchedLocation = locations.find(
      (l) =>
        String(l.location_name)
          .trim()
          .toLowerCase() ===
        String(
          wc.location ||
          wc.workcenter_location ||
          ""
        )
          .trim()
          .toLowerCase()
    );

    console.log("ALL LOCATIONS =", locations);

    console.log("WC LOCATION =", wc.location);

    console.log(
      "WC WORKCENTER LOCATION =",
      wc.workcenter_location
    );

    console.log(
      "MATCHED LOCATION =",
      matchedLocation
    );

    setSelectedLocationId(
      matchedLocation?.id || ""
    );

    setSelectedLocationName(
      matchedLocation?.location_name ||
      wc.location ||
      wc.workcenter_location ||
      ""
    );

    // =====================================
    // CLASSIFICATIONS
    // =====================================

    if (
      wc.classifications &&
      wc.classifications.length > 0
    ) {

      setClassificationRows(
        wc.classifications.map((item) => ({
          workcenterclassification:
            item.workcenterclassification || "",

          workcenterclassificationuom:
            item.workcenterclassificationuom || "",
        }))
      );

    } else {

      setClassificationRows([
        {
          workcenterclassification: "",
          workcenterclassificationuom: "",
        },
      ]);

    }

    // =====================================
    // ACTIVITIES
    // =====================================

    if (
      wc.activities &&
      wc.activities.length > 0
    ) {

      setActivityRows(wc.activities);

    } else {

      setActivityRows([
        {
          workcenteractivity: "setup",
          workcenteractivityuom:
            wc.setup_uom || "",
          is_selected:
            wc.setup ? 1 : null,
        },
        {
          workcenteractivity: "machine",
          workcenteractivityuom:
            wc.machine_uom || "",
          is_selected:
            wc.machine ? 1 : null,
        },
        {
          workcenteractivity: "labour",
          workcenteractivityuom:
            wc.labour_uom || "",
          is_selected:
            wc.labour ? 1 : null,
        },
        {
          workcenteractivity: "overhead",
          workcenteractivityuom:
            wc.overhead_uom || "",
          is_selected:
            wc.overhead ? 1 : null,
        },
      ]);

    }

    // =====================================
    // MACHINE ROWS
    // =====================================

    if (
      wc.machines &&
      wc.machines.length > 0
    ) {

      setMachineRows(

        wc.machines.map((mc) => ({

          machine_name:
            mc.machine_name || "",

          workcenter_description:
            mc.workcenter_description || "",

          person_responsible:
            mc.person_responsible || "",

          workcenter_location:
            mc.workcenter_location || "",

          start_time:
            (mc.start_time || "")
              .slice(0, 5),

          end_time:
            (mc.end_time || "")
              .slice(0, 5),

          break_mins:
            mc.break_time || "",

          capacity_utilization:
            mc.capacity_utilization || "",

          capacity_by_length:
            mc.capacity_by_length || "",

          available_capacity:
            mc.available_capacity || ""

        }))

      );

    } else {

      setMachineRows([]);

    }

    // =====================================
    // NORMALIZATION
    // =====================================

    const normalizedWc = { ...wc };

    // NOTE: workcenter_category / capacity_base_unit are intentionally left
    // in their original case here — the dropdown <option> values come from
    // the master config lists (e.g. "ABC", "Hour") and are case-sensitive,
    // so lowercasing them broke the select from matching and pre-selecting.

    // =====================================
    // FORM SET
    // =====================================

    setForm({

      ...initialFormState,

      ...normalizedWc,

      workcenter_name:
        wc.workcenter_name || "",

      workcenter_description:
        wc.workcenter_description || "",

      location:
        wc.location ||
        wc.workcenter_location ||
        "",

      person_responsible:
        wc.person_responsible || "",

      no_of_capacity:
        wc.machines?.length || 0,

      start_time:
        (wc.start_time || "")
          .slice(0, 5),

      end_time:
        (wc.end_time || "")
          .slice(0, 5),

      start_date:
        wc.start_date
          ? wc.start_date.split("T")[0]
          : "",

      end_date:
        wc.end_date
          ? wc.end_date.split("T")[0]
          : "",

      breakdown_start_date:
        wc.breakdown_start_date
          ? wc.breakdown_start_date
            .split("T")[0]
          : "",

      breakdown_end_date:
        wc.breakdown_end_date
          ? wc.breakdown_end_date
            .split("T")[0]
          : "",

      breakdown_start_time:
        (
          wc.breakdown_start_time || ""
        ).slice(0, 5),

      breakdown_end_time:
        (
          wc.breakdown_end_time || ""
        ).slice(0, 5),

    });

    // =====================================
    // ENABLE EDIT MODE
    // =====================================

    setIsDraftView(false);

    setIsEditMode(true);

    // =====================================
    // OPEN MODAL
    // =====================================

    setShowDraftModal(false);

    setShowModal(true);

    setActiveTab(0);

  };

  const openEditModal = async (wc) => {

    await loadCapacityCategory();

    await loadFactoryCalendar();

    await loadWorkcenterCategory();

    setEditingId(wc.id);

    // =====================================
    // CHANGE LOGS
    // =====================================

    try {

      const logRes = await axios.get(
        `${MASTER_API}/workcenter/change-logs/${wc.id}`
      );

      setWorkcenterLogs(logRes.data);

    } catch (err) {

      console.error("Log fetch error:", err);

    }

    // =====================================
    // LOCATION MATCH
    // =====================================

    const matchedLocation = locations.find(
      (l) =>
        String(l.location_name)
          .trim()
          .toLowerCase() ===
        String(
          wc.location ||
          wc.workcenter_location ||
          ""
        )
          .trim()
          .toLowerCase()
    );

    setSelectedLocationId(
      matchedLocation?.id || ""
    );

    setSelectedLocationName(
      matchedLocation?.location_name ||
      wc.location ||
      wc.workcenter_location ||
      ""
    );

    console.log("ALL LOCATIONS =", locations);

    console.log("WC LOCATION =", wc.location);

    console.log(
      "WC WORKCENTER LOCATION =",
      wc.workcenter_location
    );

    console.log(
      "MATCHED LOCATION =",
      matchedLocation
    );

    // =====================================
    // CLASSIFICATIONS
    // =====================================

    if (
      wc.classifications &&
      wc.classifications.length > 0
    ) {

      setClassificationRows(
        wc.classifications.map((item) => ({
          workcenterclassification:
            item.workcenterclassification || "",

          workcenterclassificationuom:
            item.workcenterclassificationuom || "",
        }))
      );

    } else {

      setClassificationRows([
        {
          workcenterclassification: "",
          workcenterclassificationuom: "",
        },
      ]);

    }

    // =====================================
    // ACTIVITIES
    // =====================================

    if (
      wc.activities &&
      wc.activities.length > 0
    ) {

      setActivityRows(wc.activities);

    } else {

      setActivityRows([
        {
          workcenteractivity: "setup",
          workcenteractivityuom: "",
          is_selected: null,
        },
        {
          workcenteractivity: "machine",
          workcenteractivityuom: "",
          is_selected: null,
        },
        {
          workcenteractivity: "labour",
          workcenteractivityuom: "",
          is_selected: null,
        },
        {
          workcenteractivity: "overhead",
          workcenteractivityuom: "",
          is_selected: null,
        },
      ]);

    }

    // =====================================
    // MACHINE ROWS
    // =====================================

    if (
      wc.machines &&
      wc.machines.length > 0
    ) {

      setMachineRows(

        wc.machines.map((mc) => ({

          machine_name:
            mc.machine_name || "",

          workcenter_description:
            mc.workcenter_description || "",

          person_responsible:
            mc.person_responsible || "",

          workcenter_location:
            mc.workcenter_location || "",

          start_time:
            (mc.start_time || "")
              .slice(0, 5),

          end_time:
            (mc.end_time || "")
              .slice(0, 5),

          break_mins:
            mc.break_time || "",

          capacity_utilization:
            mc.capacity_utilization || "",

          capacity_by_length:
            mc.capacity_by_length || "",

          available_capacity:
            mc.available_capacity || ""

        }))

      );

    } else {

      setMachineRows([]);

    }

    // =====================================
    // NORMALIZATION
    // =====================================

    const normalizedWc = { ...wc };

    // NOTE: workcenter_category / capacity_category / factory_calendar /
    // capacity_base_unit are intentionally left in original case here —
    // the dropdown <option> values come from the master config lists
    // (e.g. "ABC", "Karnataka", "Hour") and are case-sensitive, so
    // lowercasing them broke the select from matching and pre-selecting.

    // =====================================
    // FORM SET
    // =====================================

    setIsDraftView(false);

    setForm({

      ...initialFormState,

      ...normalizedWc,

      workcenter_name:
        wc.workcenter_name || "",

      workcenter_description:
        wc.workcenter_description || "",

      location:
        wc.location ||
        wc.workcenter_location ||
        "",

      person_responsible:
        wc.person_responsible || "",

      no_of_capacity:
        wc.machines?.length || 0,

      capacity_category:
        wc.capacity_category || "",

      factory_calendar:
        wc.factory_calendar || "",

      start_time:
        (wc.start_time || "")
          .slice(0, 5),

      end_time:
        (wc.end_time || "")
          .slice(0, 5),

      start_date:
        wc.start_date
          ? wc.start_date.split("T")[0]
          : "",

      end_date:
        wc.end_date
          ? wc.end_date.split("T")[0]
          : "",

      breakdown_start_date:
        wc.breakdown_start_date
          ? wc.breakdown_start_date
            .split("T")[0]
          : "",

      breakdown_end_date:
        wc.breakdown_end_date
          ? wc.breakdown_end_date
            .split("T")[0]
          : "",

      breakdown_start_time:
        (
          wc.breakdown_start_time || ""
        ).slice(0, 5),

      breakdown_end_time:
        (
          wc.breakdown_end_time || ""
        ).slice(0, 5),

    });

    // =====================================
    // OPEN MODAL
    // =====================================

    setActiveTab(0);

    setIsEditMode(false);

    setShowModal(true);

    setMessage(null);

  };

  const parseTimeToMinutes = (timeStr) => {
    if (!timeStr || typeof timeStr !== "string") return null;
    const t = timeStr.trim();
    const ampmMatch = t.match(/(\d{1,2}):(\d{2})\s*([AaPp][Mm])/);
    if (ampmMatch) {
      let hh = Number(ampmMatch[1]);
      const mm = Number(ampmMatch[2]);
      const ampm = ampmMatch[3].toLowerCase();
      if (hh === 12) hh = 0;
      if (ampm === "pm") hh += 12;
      return hh * 60 + mm;
    }
    const twentyFour = t.match(/^(\d{1,2}):(\d{2})$/);
    if (twentyFour) {
      let hh = Number(twentyFour[1]);
      const mm = Number(twentyFour[2]);
      if (hh < 0) hh = 0;
      if (hh > 23) hh = hh % 24;
      return hh * 60 + mm;
    }
    const dt = Date.parse(`1970-01-01T${t}`);
    if (!isNaN(dt)) {
      const d = new Date(dt);
      return d.getUTCHours() * 60 + d.getUTCMinutes();
    }
    return null;
  };

  // const calculateAvailableCapacity = () => {
  //   const startM = parseTimeToMinutes(form.start_time);
  //   const endM = parseTimeToMinutes(form.end_time);

  //   if (startM === null || endM === null) return "";

  //   let diffMinutes = endM - startM;
  //   if (diffMinutes <= 0) diffMinutes += 24 * 60;

  //   const breakTime = Number(form.break_time || 0);
  //   const effectiveMinutes = Math.max(0, diffMinutes - breakTime);

  //   const utilization = Number(form.capacity_utilization || 0) / 100;
  //   const noOfCap = Number(form.no_of_capacity || 0);

  //   const availableMinutes = effectiveMinutes * utilization * noOfCap;

  //   if (isNaN(availableMinutes) || !isFinite(availableMinutes)) return "";

  //   const hours = availableMinutes / 60;
  //   const mins = Math.round(availableMinutes);

  //   return `${hours.toFixed(2)} h (${mins} mins)`;
  // };

  const loadWorkcenterConfig = async () => {

    try {

      const res = await axios.get(
        `${MASTER_API}/workcenter/machine-config`
      );

      setWorkcenterConfigList(
        res.data.data || []
      );

    } catch (err) {

      console.error("loadWorkcenterConfig:", err);

    }

  };

  const loadPersonResponsible = async () => {
    try {

      const res = await axios.get(
        `${MASTER_API}/configuration/person-responsible`
      );

      setPersonResponsibleList(res.data || []);

    } catch (err) {

      console.error("loadPersonResponsible:", err);

    }
  };

  const loadCapacityResponsible = async () => {
    try {

      const res = await axios.get(
        `${MASTER_API}/configuration/capacity-responsible`
      );

      setCapacityResponsibleList(res.data || []);

    } catch (err) {

      console.error("loadCapacityResponsible:", err);

    }
  };

  const loadCostCenter = async () => {
    try {

      const res = await axios.get(
        `${MASTER_API}/configuration/cost-center`
      );

      setCostCenterList(res.data || []);

    } catch (err) {

      console.error("loadCostCenter:", err);

    }
  };

  const loadControllingArea = async () => {
    try {

      const res = await axios.get(
        `${MASTER_API}/configuration/controlling-area`
      );

      setControllingAreaList(res.data || []);

    } catch (err) {

      console.error("loadControllingArea:", err);

    }
  };
  const loadWorkcenterCategory = async () => {
    try {

      const res = await axios.get(
        `${MASTER_API}/configuration/dynamic/workcenter-category`
      );

      setWorkcenterCategoryList(res.data || []);

    } catch (err) {

      console.error("loadWorkcenterCategory:", err);

    }
  };

  const loadCapacityCategory = async () => {
    try {

      const res = await axios.get(
        `${MASTER_API}/configuration/dynamic/capacity-category`
      );

      setCapacityCategoryList(res.data || []);

    } catch (err) {

      console.error("loadCapacityCategory:", err);

    }
  };

  const loadFactoryCalendar = async () => {
    try {

      const res = await axios.get(
        `${MASTER_API}/configuration/dynamic/factory-calendar`
      );

      setFactoryCalendarList(res.data || []);

    } catch (err) {

      console.error("loadFactoryCalendar:", err);

    }
  };
  const loadUom = async () => {
    try {

      const res = await axios.get(
        `${MASTER_API}/configuration/uom`
      );

      setUomList(res.data || []);

    } catch (err) {

      console.error("loadUom:", err);

    }
  };

  const loadPlants = async () => {
    try {
      const res = await axios.get(`${MASTER_API}/workcenter/location`);
      const data = res.data;
      setLocations(Array.isArray(data) ? data : data.data || []);
    } catch (err) {
      console.error("loadPlants:", err);
    }
  };

  const loadWorkcenters = async () => {
    try {
      const res = await axios.get(`${MASTER_API}/workcenter`);
      const data = res.data;
      setWorkcenters(data.data || []);
    } catch (err) {
      console.error("loadWorkcenters:", err);
    }
  };

  // Soft-deletes a workcenter (status -> 'Deleted'). Backend keeps the row
  // and its related workcenter_machine / workcenter_classification /
  // change_logs rows for audit — it just stops returning it from
  // GET /workcenter, so it disappears from this list and everywhere it's
  // referenced from here.
  const openDeleteConfirm = (wc) => setDeleteTarget(wc);
  const closeDeleteConfirm = () => {
    if (isDeleting) return;
    setDeleteTarget(null);
  };

  const confirmDeleteWorkcenter = async () => {
    if (!deleteTarget) return;
    setIsDeleting(true);
    try {
      await axios.delete(`${MASTER_API}/workcenter/delete/${deleteTarget.id}`);
      setWorkcenters((prev) => prev.filter((item) => item.id !== deleteTarget.id));
      setMessage({ type: "success", text: "Workcenter deleted successfully" });
      setDeleteTarget(null);
    } catch (err) {
      console.error("confirmDeleteWorkcenter:", err);
      setMessage({
        type: "error",
        text: err.response?.data?.message || "Failed to delete workcenter",
      });
    } finally {
      setIsDeleting(false);
    }
  };

  const updateForm = (patch) => setForm((f) => ({ ...f, ...patch }));

  const addClassificationRow = () => {
    setClassificationRows((prev) => [
      ...prev,
      {
        workcenterclassification: "",
        workcenterclassificationuom: "",
      },
    ]);
  };

  const removeClassificationRow = (index) => {
    setClassificationRows((prev) =>
      prev.filter((_, i) => i !== index)
    );
  };

  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;

  const filteredWorkcenters = workcenters.filter((wc) => {

    const matchLocation =
      !selectedLocationId ||
      wc.location === selectedLocationName;

    const matchWorkcenter =
      !searchWorkcenter ||
      wc.workcenter_name
        ?.toLowerCase()
        .includes(searchWorkcenter.toLowerCase());

    const matchDescription =
      !searchDescription ||
      wc.workcenter_description
        ?.toLowerCase()
        .includes(searchDescription.toLowerCase());

    return (
      matchLocation &&
      matchWorkcenter &&
      matchDescription
    );
  });

  const totalPages = Math.ceil(filteredWorkcenters.length / itemsPerPage);

  const paginatedWorkcenters = filteredWorkcenters.slice(
    startIndex,
    endIndex
  );
  const handleLocationChange = (e) => {

    const id = e.target.value;

    setSelectedLocationId(id);

    const loc = locations.find(
      (l) => String(l.id) === String(id)
    );

    setSelectedLocationName(loc?.location_name || "");

  };

  const openCreateModal = () => {
    if (!selectedLocationId) {
      setMessage({ type: "error", text: "Please select a location first." });
      return;
    }
    setEditingId(null);
    setIsEditMode(true);
    setIsDraftView(false);
    setForm(initialFormState);


    setMachineRows([]);
    setClassificationRows([
      {
        workcenterclassification: "",
        workcenterclassificationuom: "",
      },
    ]);

    setActivityRows([
      {
        workcenteractivity: "setup",
        workcenteractivityuom: "",
        is_selected: null,
      },
      {
        workcenteractivity: "machine",
        workcenteractivityuom: "",
        is_selected: null,
      },
      {
        workcenteractivity: "labour",
        workcenteractivityuom: "",
        is_selected: null,
      },
      {
        workcenteractivity: "overhead",
        workcenteractivityuom: "",
        is_selected: null,
      },
    ]);
    setActiveTab(0);
    setShowModal(true);
    setMessage(null);
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingId(null);
    setMessage(null);
    setMachineRows([]);
    if (isDraftView) {
      setShowDraftModal(true);
      setIsDraftView(false);
    }

    // Handle navigation back to the previous screen
    const queryParams = new URLSearchParams(location.search);
    const returnTo = queryParams.get("returnTo");

    if (returnTo) {
      navigate(returnTo);
    } else if (queryParams.get("view")) {
      // Clean up the URL if we just dismiss the modal manually
      navigate("/cable/master-data/workcenter", { replace: true });
    }
  };

  const resetForm = () => {
    setForm(initialFormState);
  };

  const handleSave = async () => {
    setMessage(null);

    if (!selectedLocationId && !editingId) {
      console.error("🚫 BLOCKED: Location is missing!");
      setMessage({ type: "error", text: "Location missing." });
      setActiveTab(0);
      return;
    }
    // if (!form.workcenter_category) {
    //   console.error("🚫 BLOCKED: Category is missing!");
    //   setMessage({ type: "error", text: "Select workcenter category in Basic Data." });
    //   setActiveTab(0);
    //   return;
    // }
    if (!form.workcenter_name) {
      console.error("🚫 BLOCKED: Workcenter Name is missing!");
      setMessage({ type: "error", text: "Provide Workcenter Name." });
      setActiveTab(0);
      return;
    }

    console.log("✅ Validation Passed! Hitting the API now...");
    console.log("MACHINES IN PAYLOAD =", JSON.stringify(machineRows, null, 2));

    const getAvailableCapacityNumber = () => {
      const startM = parseTimeToMinutes(form.start_time);
      const endM = parseTimeToMinutes(form.end_time);
      if (startM === null || endM === null) return null;

      let diffMinutes = endM - startM;
      if (diffMinutes <= 0) diffMinutes += 24 * 60;

      const breakT = Number(form.break_time || 0);
      const effectiveMinutes = Math.max(0, diffMinutes - breakT);
      const util = Number(form.capacity_utilization || 0) / 100;
      const noOfCap = Number(form.no_of_capacity || 0);

      const availableMins = effectiveMinutes * util * noOfCap;
      if (isNaN(availableMins) || !isFinite(availableMins)) return null;

      return (availableMins / 60).toFixed(2); // Returns "67.50"
    };

    const payload = {
      ...form,

      location: selectedLocationName,
      location_id: selectedLocationId,

      classifications: classificationRows,
      setup:
        activityRows.find(
          x => x.workcenteractivity === "setup"
        )?.is_selected || null,

      setup_uom:
        activityRows.find(
          x => x.workcenteractivity === "setup"
        )?.workcenteractivityuom || "",

      machine:
        activityRows.find(
          x => x.workcenteractivity === "machine"
        )?.is_selected || null,

      machine_uom:
        activityRows.find(
          x => x.workcenteractivity === "machine"
        )?.workcenteractivityuom || "",

      labour:
        activityRows.find(
          x => x.workcenteractivity === "labour"
        )?.is_selected || null,

      labour_uom:
        activityRows.find(
          x => x.workcenteractivity === "labour"
        )?.workcenteractivityuom || "",

      overhead:
        activityRows.find(
          x => x.workcenteractivity === "overhead"
        )?.is_selected || null,

      overhead_uom:
        activityRows.find(
          x => x.workcenteractivity === "overhead"
        )?.workcenteractivityuom || "",
      machines: machineRows.map((row) => ({
        ...row,

        break_time:
          row.break_mins === ""
            ? null
            : Number(row.break_mins),

        capacity_utilization:
          row.capacity_utilization === ""
            ? null
            : Number(row.capacity_utilization),

        capacity_by_length:
          row.capacity_by_length === ""
            ? null
            : Number(row.capacity_by_length),

        available_capacity:
          row.available_capacity === ""
            ? null
            : Number(row.available_capacity),

        start_time: row.start_time || null,
        end_time: row.end_time || null,
      })),

      break_time: form.break_time === "" ? null : Number(form.break_time),
      capacity_utilization: form.capacity_utilization === "" ? null : Number(form.capacity_utilization),
      no_of_capacity: form.no_of_capacity === "" ? null : Number(form.no_of_capacity),
      capacity_by_length: form.capacity_by_length === "" ? null : Number(form.capacity_by_length),
      no_of_machines_breakdown: form.no_of_machines_breakdown === "" ? null : Number(form.no_of_machines_breakdown),
      start_date: form.start_date || null,
      end_date: form.end_date || null,
      start_time: form.start_time || null,
      end_time: form.end_time || null,
      breakdown_start_date: form.breakdown_start_date || null,
      breakdown_end_date: form.breakdown_end_date || null,
      breakdown_start_time: form.breakdown_start_time || null,
      breakdown_end_time: form.breakdown_end_time || null,
      available_capacity: machineRows.length > 0
        ? (parseFloat(form.available_capacity) || null)
        : getAvailableCapacityNumber()
    };

    try {
      setSaving(true);

      let url = `${MASTER_API}/workcenter/add`;
      let method = "POST";

      if (editingId) {
        url = `${MASTER_API}/workcenter/update/${editingId}`;
        method = "PUT";
      }

      let res, data;
      try {
        const r = await axios({ url, method, data: payload });
        res = { ok: true };
        data = r.data;
      } catch (e) {
        if (!e.response) throw e;
        res = { ok: false };
        data = e.response.data || {};
      }

      if (!res.ok || data.success === false) {
        setMessage({ type: "error", text: data.message || "Save failed" });
      } else {
        // Save machine rows to workcenter_machines table
        // const savedWorkcenterId = editingId || data.id;
        // if (machineRows.length > 0 && savedWorkcenterId) {
        //   await saveMachinesToDB(savedWorkcenterId);
        // }

        setMessage({ type: "success", text: data.message || (editingId ? "Updated" : "Created") });
        resetForm();
        setShowModal(false);
        setEditingId(null);
        await loadWorkcenters();
      }
    } catch (err) {
      console.error("save error", err);
      setMessage({ type: "error", text: "Server error" });
    } finally {
      setSaving(false);
    }
  };

  // form mode -> field rules
  const wcMode = isDraftView ? "view_draft" : !editingId ? "create" : isEditMode ? "edit" : "view";
  const fs = (field, mode = wcMode) => access.fieldState(field, mode);
  // list-table cell: MaskedValue when the user's Display role hides the column
  const mv = (field, value) => (fs(field, "view").masked ? <MaskedValue height={22} /> : value);

  if (access.loading) {
    return <Layout><div style={{ padding: 20, fontSize: 13, color: "#64748b" }}>Loading permissions…</div></Layout>;
  }
  if (!access.canView && !access.canCreate) {
    return (
      <Layout>
        <div style={{ padding: 20 }}>
          <div style={{ fontSize: 26, fontWeight: 700, color: "#1e293b" }}>Work Center</div>
          <div style={{ fontSize: 13, color: "#64748b" }}>
            {access.error
              ? `Could not load your permissions (${access.error}).`
              : "Your role doesn't include the Work Center module (MD_WORKCENTER). Ask an administrator to add it to your role, then log in again."}
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div style={{ padding: 20 }}>

        {/* ===================== HEADER ===================== */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
          <div>
            <div style={{ fontSize: 26, fontWeight: 700, color: '#1e293b' }}>Work Center</div>
            <div style={{ fontSize: 13, color: '#64748b' }}>Manage and view work centers</div>
          </div>
          <div style={{ display: 'flex', gap: '10px' }}>
            <button
              disabled={!canCreate}
              onClick={() => setShowUploadModal(true)}
              className="btn btn-outline-dark"
              style={{ padding: "8px 16px", background: "white", color: "#1e293b", border: "1px solid #1e293b", borderRadius: 4, fontWeight: 600, cursor: "pointer" }}
            >
              ☁️ Create Work Center By Upload
            </button>
            <button
              className="btn btn-dark"
              disabled={!canCreate}
              onClick={openCreateModal}
              style={{ padding: "8px 16px", background: "#1e293b", color: "white", border: "none", borderRadius: 4, cursor: "pointer" }}
            >
              + Create Work Center
            </button>
          </div>
        </div>

        {/* ===================== SELECT LOCATION BOX ===================== */}
        <div style={{ background: '#ffffff', borderRadius: 8, padding: 15, boxShadow: '0 1px 4px rgba(0,0,0,0.08)', marginBottom: 15 }}>
          <div className="row g-3">

            {/* LOCATION */}
            <div className="col-md-4">
              <label style={{ fontSize: "12px", fontWeight: 600, marginBottom: "2px", display: 'block', color: '#334155' }}>
                Location
              </label>

              <select
                value={selectedLocationId || ""}
                onChange={handleLocationChange}
                style={{
                  width: "100%",
                  fontSize: "13px",
                  height: "32px",
                  padding: "4px 8px",
                  border: "1px solid #dee2e6",
                  borderRadius: "4px"
                }}
              >
                <option value="">
                  Select Location
                </option>

                {locations.map((loc) => (
                  <option
                    key={loc.id}
                    value={loc.id}
                  >
                    {loc.location_name}
                  </option>
                ))}
              </select>
            </div>

            {/* WORKCENTER NAME */}
            <div className="col-md-4">
              <label style={{ fontSize: "12px", fontWeight: 600, marginBottom: "2px", display: 'block', color: '#334155' }}>
                Workcenter Name
              </label>

              <input
                type="text"
                placeholder="Search Workcenter"
                value={searchWorkcenter}
                onChange={(e) => setSearchWorkcenter(e.target.value)}
                style={{
                  width: "100%",
                  fontSize: "13px",
                  height: "32px",
                  padding: "4px 8px",
                  border: "1px solid #dee2e6",
                  borderRadius: "4px"
                }}
              />
            </div>

            {/* DESCRIPTION */}
            <div className="col-md-4">
              <label style={{ fontSize: "12px", fontWeight: 600, marginBottom: "2px", display: 'block', color: '#334155' }}>
                Description
              </label>

              <input
                type="text"
                placeholder="Search Description"
                value={searchDescription}
                onChange={(e) => setSearchDescription(e.target.value)}
                style={{
                  width: "100%",
                  fontSize: "13px",
                  height: "32px",
                  padding: "4px 8px",
                  border: "1px solid #dee2e6",
                  borderRadius: "4px"
                }}
              />
            </div>

          </div>
        </div>

        {message && (
          <div
            style={{
              marginBottom: 16,
              padding: 10,
              background: message.type === "error" ? "#ffe6e6" : "#e8f7ea",
              color: message.type === "error" ? "#d63031" : "#1b5e20",
              borderRadius: 6,
              fontSize: 13,
            }}
          >
            {message.text}
          </div>
        )}

        {/* ===================== DATA TABLE ===================== */}
        <div
          style={{
            width: "100%",
            background: "white",
            borderRadius: 6,
            overflowX: "auto",
            fontSize: "13px",
          }}
        >
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead>
              <tr style={{ background: "#f1f1f1" }}>
                <th style={thStyle}>Workcenter Name</th>
                <th style={thStyle}>Workcenter Description</th>
                <th style={thStyle}>Location</th>
                <th style={thStyle}>Category</th>
                <th style={thStyle}>Cost Center</th>
                <th style={thStyle}></th>
              </tr>
            </thead>

            <tbody>
              {workcenters.length === 0 ? (
                <tr>
                  <td colSpan="6" style={{ padding: 20, textAlign: "center", color: "#777" }}>
                    No Work Centers found
                  </td>
                </tr>
              ) : (
                paginatedWorkcenters.map((wc) => (
                  <tr key={wc.id}>
                    <td style={tdStyle}>{mv("workcenter_name", wc.workcenter_name)}</td>
                    <td style={tdStyle}>{mv("workcenter_description", wc.workcenter_description)}</td>
                    <td style={tdStyle}>
                      {mv("location", wc.location || "-")}
                    </td>
                    <td style={tdStyle}>{mv("workcenter_category", wc.workcenter_category)}</td>

                    <td style={tdStyle}>{mv("cost_center", wc.cost_center ?? "-")}</td>
                    <td style={tdStyle}>
                      <div style={{ display: "flex", gap: 6 }}>
                        <button
                          onClick={() => openEditModal(wc)}
                          className="btn btn-outline-dark btn-sm"
                          style={{
                            fontSize: "11px",
                            padding: "2px 8px",
                          }}
                        >
                          View
                        </button>

                        <button
                          disabled={!canEdit}
                          onClick={() => openDeleteConfirm(wc)}
                          title="Delete Workcenter"
                          style={{
                            display: "inline-flex",
                            alignItems: "center",
                            justifyContent: "center",
                            width: 28,
                            height: 28,
                            padding: 0,
                            border: "1px solid #fecaca",
                            borderRadius: 6,
                            background: "#fee2e2",
                            cursor: "pointer",
                          }}
                        >
                          <svg
                            width="14"
                            height="14"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="#dc2626"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          >
                            <polyline points="3 6 5 6 21 6" />
                            <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
                            <path d="M10 11v6" />
                            <path d="M14 11v6" />
                            <path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2" />
                          </svg>
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        <div style={{ display: "flex", justifyContent: "flex-end", alignItems: "center", gap: "10px", marginTop: "15px", width: "100%" }}>  <button className="btn btn-light" disabled={currentPage === 1} onClick={() => setCurrentPage(currentPage - 1)}  >    {"<"}  </button>  <span>    Page {currentPage} of {totalPages}  </span>  <button className="btn btn-light" disabled={currentPage === totalPages} onClick={() => setCurrentPage(currentPage + 1)}  >    {">"}  </button></div>

        {/* ===================== CREATE / EDIT MODAL ===================== */}
        {showModal && (
          <>
            <div
              onClick={closeModal}
              style={{
                position: "fixed",
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                background: "rgba(0,0,0,0.5)",
                zIndex: 1050,
              }}
            />

            <div
              role="dialog"
              aria-modal="true"
              onClick={(e) => e.stopPropagation()}
              style={{
                position: "fixed",
                top: "50%",
                left: "50%",
                transform: "translate(-50%, -50%)",
                width: "95%",
                maxWidth: 900,
                maxHeight: "90vh",
                overflowY: "auto",
                overflowX: "hidden",
                background: "white",
                zIndex: 1055,
                borderRadius: 8,
                boxShadow: "0 0 20px rgba(0,0,0,0.3)",
                display: "flex",
                flexDirection: "column",
              }}
            >
              <div style={{ padding: "12px 20px", borderBottom: "1px solid #ddd", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <h3 style={{ margin: 0, color: '#1e293b', fontSize: '18px', fontWeight: 600 }}>
                  {isDraftView ? `Draft Work Center Preview (${form.workcenter_name})` : editingId ? "Edit Work Center" : "Create Work Center"}
                </h3>
                <button
                  onClick={closeModal}
                  style={{
                    background: "transparent",
                    border: "none",
                    cursor: "pointer",
                    fontSize: 16,
                    color: "#64748b"
                  }}
                >
                  ✖
                </button>
              </div>

              <div style={{ padding: "16px 20px", overflowY: "auto" }}>

              <div style={{ marginTop: 0 }}>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
                  <FieldBox st={fs("workcenter_name")} label="Workcenter Name *">
                    <input
                      type="text"
                      value={form.workcenter_name || ""}
                      onChange={(e) =>
                        updateForm({ workcenter_name: e.target.value })
                      }
                      placeholder="Enter Workcenter Name"
                      style={compactInput}
                      disabled={isDraftView || !isEditMode}
                    />
                  </FieldBox>

                  <FieldBox st={fs("workcenter_description")} label="Workcenter Description">
                    <input
                      type="text"
                      value={form.workcenter_description || ""}
                      onChange={(e) =>
                        updateForm({ workcenter_description: e.target.value })
                      }
                      placeholder="Enter Workcenter Description"
                      style={compactInput}
                      disabled={isDraftView || !isEditMode}
                    />
                  </FieldBox>
                </div>
              </div>

              <div style={{ display: "flex", gap: 5, marginBottom: 15, marginTop: 14, flexWrap: "wrap" }}>
                <TabButton index={0} activeTab={activeTab} setActiveTab={setActiveTab} label="Basic Data" />
                <TabButton index={1} activeTab={activeTab} setActiveTab={setActiveTab} label="Capacity" />
                <TabButton index={2} activeTab={activeTab} setActiveTab={setActiveTab} label="Classification" />
                <TabButton index={3} activeTab={activeTab} setActiveTab={setActiveTab} label="Costing" />

                {editingId && (
                  <TabButton
                    index={4}
                    activeTab={activeTab}
                    setActiveTab={setActiveTab}
                    label="Change Log"
                  />
                )}
              </div>
              <hr style={{ margin: "10px 0" }} />

              <div style={{ marginTop: 6 }}>

                {activeTab === 0 && (
                  <>
                    <div style={gridTwoCols}>
                      <FieldBox st={fs("workcenter_category")} label="Workcenter Category">
                        {/* Added || "" to prevent React null warnings */}
                        <select
                          value={form.workcenter_category || ""}
                          onChange={(e) => updateForm({ workcenter_category: e.target.value })}
                          style={compactInput}
                          disabled={isDraftView || !isEditMode}
                        >
                          <option value="">-- Select --</option>
                          {workcenterCategoryList.map((item) => (
                            <option key={item.id} value={item.name}>
                              {item.name}
                            </option>
                          ))}
                        </select>
                      </FieldBox>

                      <FieldBox st={fs("person_responsible")} label="Person Responsible">
                        {/* Added || "" to prevent React null warnings */}
                        <select
                          value={form.person_responsible || ""}
                          onChange={(e) => updateForm({ person_responsible: e.target.value })}
                          style={compactInput}
                          disabled={isDraftView || !isEditMode}
                        >
                          <option value="">-- Select --</option>
                          {personResponsibleList.map((item) => (
                            <option key={item.id} value={item.name}>
                              {item.name}
                            </option>
                          ))}
                        </select>
                      </FieldBox>

                      <FieldBox st={fs("location")} label="Location">
                        {/* CHANGED THIS FROM READ-ONLY INPUT TO A DROPDOWN! */}
                        <select
                          value={selectedLocationId || ""}
                          onChange={handleLocationChange}
                          style={compactInput}
                          disabled={isDraftView || !isEditMode}
                        >
                          <option value="">-- Select Location --</option>

                          {locations.map((loc) => (
                            <option key={loc.id} value={loc.id}>
                              {loc.location_name}
                            </option>
                          ))}
                        </select>
                      </FieldBox>
                    </div>
                  </>
                )}

                {activeTab === 2 && (
                  <TableGuard st={fs("classifications")}>
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "flex-end",
                        marginBottom: "8px",
                      }}
                    >
                      <button
                        type="button"
                        onClick={addClassificationRow}
                        disabled={isDraftView || !isEditMode}
                        style={{
                          background: "#021843",
                          color: "#fff",
                          border: "none",
                          padding: "5px 12px",
                          borderRadius: "4px",
                          cursor: "pointer",
                          fontSize: 13,
                          fontWeight: 600,
                        }}
                      >
                        + Add
                      </button>
                    </div>
                    <table style={styles.erpTable}>
                      <thead>
                        <tr>
                          <th style={{ ...styles.erpTh, width: "45%" }}>Classification Name</th>
                          <th style={{ ...styles.erpTh, width: "45%" }}>UOM</th>
                          <th style={{ ...styles.erpTh, width: "10%" }}></th>
                        </tr>
                      </thead>
                      <tbody>
                        {classificationRows.map((row, idx) => (
                          <tr key={idx}>
                            <td style={styles.erpTd}>
                              <input
                                type="text"
                                value={row.workcenterclassification || ""}
                                onChange={(e) => {
                                  const updated = [...classificationRows];
                                  updated[idx].workcenterclassification = e.target.value;
                                  setClassificationRows(updated);
                                }}
                                className="form-control"
                                style={compactInput}
                                disabled={isDraftView || !isEditMode}
                                placeholder="Classification"
                              />
                            </td>

                            <td style={styles.erpTd}>
                              <Select
                                options={uomList.map((uom) => ({
                                  value: uom.name,
                                  label: uom.name,
                                }))}

                                value={
                                  row.workcenterclassificationuom
                                    ? {
                                      value: row.workcenterclassificationuom,
                                      label: row.workcenterclassificationuom,
                                    }
                                    : null
                                }

                                onChange={(selected) => {
                                  const updated = [...classificationRows];

                                  updated[idx].workcenterclassificationuom =
                                    selected?.value || "";

                                  setClassificationRows(updated);
                                }}

                                isDisabled={isDraftView || !isEditMode}

                                placeholder="Select UOM"

                                isSearchable
                                isClearable

                                styles={customSelectStyles}
                              />
                            </td>

                            <td style={{ ...styles.erpTd, textAlign: "center" }}>
                              {classificationRows.length > 1 && (
                                <button
                                  type="button"
                                  onClick={() => removeClassificationRow(idx)}
                                  disabled={isDraftView || !isEditMode}
                                  style={{
                                    border: 'none',
                                    background: 'transparent',
                                    cursor: 'pointer',
                                    fontSize: '16px',
                                    color: '#dc3545'
                                  }}
                                >
                                  🗑️
                                </button>
                              )}
                            </td>

                          </tr>
                        ))}
                      </tbody>
                    </table>

                  </TableGuard>
                )}

                {activeTab === 1 && (
                  <>
                    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
                      <FieldBox st={fs("capacity_category")} label="Capacity Category">
                        <select
                          value={form.capacity_category || ""}
                          onChange={(e) =>
                            updateForm({
                              capacity_category: e.target.value
                            })
                          }
                          style={compactInput}
                          disabled={isDraftView || !isEditMode}
                        >
                          <option value="">
                            -- Select --
                          </option>

                          {capacityCategoryList.map((item) => (
                            <option
                              key={item.id}
                              value={item.name}
                            >
                              {item.name}
                            </option>
                          ))}
                        </select>
                      </FieldBox>

                      <FieldBox st={fs("capacity_responsible")} label="Capacity Responsible">
                        <select
                          value={form.capacity_responsible}
                          onChange={(e) => updateForm({ capacity_responsible: e.target.value })}
                          style={compactInput}
                          disabled={isDraftView || !isEditMode}
                        >
                          <option value="">-- Select --</option>
                          {capacityResponsibleList.map((item) => (
                            <option key={item.id} value={item.name}>
                              {item.name}
                            </option>
                          ))}
                        </select>
                      </FieldBox>

                      <FieldBox st={fs("factory_calendar")} label="Factory Calendar">
                        <select
                          value={form.factory_calendar || ""}
                          onChange={(e) =>
                            updateForm({
                              factory_calendar: e.target.value
                            })
                          }
                          style={compactInput}
                          disabled={isDraftView || !isEditMode}
                        >
                          <option value="">
                            -- Select --
                          </option>

                          {factoryCalendarList.map((item) => (
                            <option
                              key={item.id}
                              value={item.name}
                            >
                              {item.name}
                            </option>
                          ))}
                        </select>
                      </FieldBox>

                      <FieldBox st={fs("capacity_base_unit")} label="Capacity Base Unit">
                        <select
                          value={form.capacity_base_unit}
                          onChange={(e) => updateForm({ capacity_base_unit: e.target.value })}
                          style={compactInput}
                          disabled={isDraftView || !isEditMode}
                        >
                          <option value="">-- Select --</option>
                          {uomList.map((uom) => (
                            <option key={uom.id} value={uom.name}>
                              {uom.name}
                            </option>
                          ))}
                        </select>
                      </FieldBox>

                      <FieldBox st={fs("no_of_capacity")} label="No. of Capacity">
                        <input
                          type="number"
                          value={form.no_of_capacity}
                          onChange={(e) => updateForm({ no_of_capacity: e.target.value })}
                          style={compactInput}
                          disabled={isDraftView || !isEditMode}
                        />
                      </FieldBox>

                      <FieldBox st={fs("capacity_by_length")} label="Capacity By Length">
                        <input
                          type="number"
                          value={form.capacity_by_length}
                          onChange={(e) => updateForm({ capacity_by_length: e.target.value })}
                          style={compactInput}
                          disabled={isDraftView || !isEditMode}
                        />
                      </FieldBox>

                      <FieldBox st={fs("available_capacity")} label="Available Capacity">
                        <input
                          type="text"
                          value={form.available_capacity || ""}
                          readOnly
                          style={{ ...compactInput, background: "#f0fdf4", fontWeight: 600, color: "#166534" }}
                        />
                      </FieldBox>

        {Number(form.no_of_capacity) > 0 && !isDraftView && (
          <TableGuard st={fs("machines")} style={{ gridColumn: "1 / -1", marginTop: 12, border: "1px solid #e2e8f0", borderRadius: 8, padding: 14, background: "#fafbfc" }}>
            <h4 style={{ margin: "0 0 4px", fontSize: 13, color: "#1e293b" }}>
              ⚙ Configure Machines — {form.no_of_capacity} Capacity Slot{Number(form.no_of_capacity) > 1 ? "s" : ""}
            </h4>
            <p style={{ margin: "0 0 10px", fontSize: 11, color: "#64748b" }}>
              Fill details for each machine below. Available Capacity is auto-calculated and summed into the workcenter.
            </p>

            <div style={{ overflowX: "auto" }}>

                <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>

                  <thead>
                    <tr style={{ background: "#f1f5f9" }}>

                      <th style={{ padding: "10px 8px", border: "1px solid #d1d5db", textAlign: "center", width: 40 }}></th>

                      <th style={{ padding: "10px 8px", border: "1px solid #d1d5db", textAlign: "left", minWidth: 160 }}>
                        Machine
                      </th>

                      <th style={{ padding: "10px 8px", border: "1px solid #d1d5db", textAlign: "left", minWidth: 220 }}>
                        Workcenter Description
                      </th>

                      <th style={{ padding: "10px 8px", border: "1px solid #d1d5db", textAlign: "left", minWidth: 180 }}>
                        Person Responsible
                      </th>

                      <th style={{ padding: "10px 8px", border: "1px solid #d1d5db", textAlign: "left", minWidth: 160 }}>
                        Workcenter Location
                      </th>

                      <th style={{ padding: "10px 8px", border: "1px solid #d1d5db", textAlign: "left", minWidth: 110 }}>
                        Start Time
                      </th>

                      <th style={{ padding: "10px 8px", border: "1px solid #d1d5db", textAlign: "left", minWidth: 110 }}>
                        End Time
                      </th>

                      <th style={{ padding: "10px 8px", border: "1px solid #d1d5db", textAlign: "left", minWidth: 100 }}>
                        Break (mins)
                      </th>

                      <th style={{ padding: "10px 8px", border: "1px solid #d1d5db", textAlign: "left", minWidth: 130 }}>
                        Capacity Utilization (%)
                      </th>

                      <th style={{ padding: "10px 8px", border: "1px solid #d1d5db", textAlign: "left", minWidth: 120 }}>
                        Capacity by Length
                      </th>

                      <th style={{ padding: "10px 8px", border: "1px solid #d1d5db", textAlign: "left", minWidth: 140 }}>
                        Available Capacity (hrs)
                      </th>

                      <th style={{ padding: "10px 8px", border: "1px solid #d1d5db", textAlign: "left", minWidth: 150 }}>
                        Breakdown Start Date
                      </th>

                      <th style={{ padding: "10px 8px", border: "1px solid #d1d5db", textAlign: "left", minWidth: 150 }}>
                        Breakdown End Date
                      </th>

                      <th style={{ padding: "10px 8px", border: "1px solid #d1d5db", textAlign: "left", minWidth: 130 }}>
                        Breakdown Start Time
                      </th>

                      <th style={{ padding: "10px 8px", border: "1px solid #d1d5db", textAlign: "left", minWidth: 130 }}>
                        Breakdown End Time
                      </th>

                    </tr>
                  </thead>

                  <tbody>

                    {machineRows.map((row, idx) => (

                      <tr
                        key={idx}
                        style={{
                          background: idx % 2 === 0 ? "#fff" : "#f8fafc"
                        }}
                      >

                        <td style={{ padding: "8px", border: "1px solid #e5e7eb", textAlign: "center", fontWeight: 600, color: "#64748b" }}>
                          {idx + 1}
                        </td>

                        <td style={{ padding: "6px 8px", border: "1px solid #e5e7eb" }}>
                          <input
                            type="text"
                            value={row.machine_name || ""}
                            onChange={(e) =>
                              updateMachineRow(idx, {
                                machine_name: e.target.value
                              })
                            }
                            placeholder="Enter Machine"
                            style={{
                              ...compactInput,
                              height: 28
                            }}
                            disabled={isDraftView || !isEditMode}
                          />
                        </td>

                        <td style={{ padding: "6px 8px", border: "1px solid #e5e7eb" }}>
                          <input
                            type="text"
                            value={row.workcenter_description || ""}
                            onChange={(e) =>
                              updateMachineRow(idx, {
                                workcenter_description: e.target.value
                              })
                            }
                            placeholder="Enter Description"
                            style={{
                              ...compactInput,
                              height: 28
                            }}
                            disabled={isDraftView || !isEditMode}
                          />
                        </td>

                        <td style={{ padding: "6px 8px", border: "1px solid #e5e7eb" }}>
                          <input
                            type="text"
                            value={row.person_responsible || ""}
                            onChange={(e) =>
                              updateMachineRow(idx, {
                                person_responsible: e.target.value
                              })
                            }
                            placeholder="Enter Person Responsible"
                            style={{
                              ...compactInput,
                              height: 28
                            }}
                            disabled={isDraftView || !isEditMode}
                          />
                        </td>

                        <td style={{ padding: "6px 8px", border: "1px solid #e5e7eb" }}>
                          <Select
                            options={locations.map(l => ({
                              value: l.location_name,
                              label: l.location_name
                            }))}
                            value={row.workcenter_location
                              ? { value: row.workcenter_location, label: row.workcenter_location }
                              : null
                            }
                            onChange={(opt) =>
                              updateMachineRow(idx, {
                                workcenter_location: opt ? opt.value : ""
                              })
                            }
                            isClearable
                            isDisabled={isDraftView || !isEditMode}
                            placeholder="Select..."
                            menuPortalTarget={document.body}
                            styles={{
                              control: (base) => ({ ...base, fontSize: 12, minHeight: 28, height: 28, border: "1px solid #ccc", boxShadow: "none" }),
                              valueContainer: (base) => ({ ...base, padding: "0 6px" }),
                              dropdownIndicator: (base) => ({ ...base, padding: "0 4px" }),
                              indicatorsContainer: (base) => ({ ...base, height: 28 }),
                              menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                              option: (base) => ({ ...base, fontSize: 12 }),
                            }}
                          />
                        </td>

                        <td style={{ padding: "6px 8px", border: "1px solid #e5e7eb" }}>
                          <input
                            type="time"
                            value={row.start_time}
                            onChange={(e) =>
                              updateMachineRow(idx, {
                                start_time: e.target.value
                              })
                            }
                            style={{
                              ...compactInput,
                              height: 28
                            }}
                            disabled={isDraftView || !isEditMode}
                          />
                        </td>

                        <td style={{ padding: "6px 8px", border: "1px solid #e5e7eb" }}>
                          <input
                            type="time"
                            value={row.end_time}
                            onChange={(e) =>
                              updateMachineRow(idx, {
                                end_time: e.target.value
                              })
                            }
                            style={{
                              ...compactInput,
                              height: 28
                            }}
                            disabled={isDraftView || !isEditMode}
                          />
                        </td>

                        <td style={{ padding: "6px 8px", border: "1px solid #e5e7eb" }}>
                          <input
                            type="number"
                            value={row.break_mins}
                            onChange={(e) =>
                              updateMachineRow(idx, {
                                break_mins: e.target.value
                              })
                            }
                            style={{
                              ...compactInput,
                              height: 28
                            }}
                            min={0}
                            placeholder="0"
                            disabled={isDraftView || !isEditMode}
                          />
                        </td>

                        <td style={{ padding: "6px 8px", border: "1px solid #e5e7eb" }}>
                          <input
                            type="number"
                            value={row.capacity_utilization}
                            onChange={(e) =>
                              updateMachineRow(idx, {
                                capacity_utilization: e.target.value
                              })
                            }
                            style={{
                              ...compactInput,
                              height: 28
                            }}
                            min={0}
                            max={100}
                            placeholder="0"
                            disabled={isDraftView || !isEditMode}
                          />
                        </td>

                        <td style={{ padding: "6px 8px", border: "1px solid #e5e7eb" }}>
                          <input
                            type="number"
                            value={row.capacity_by_length}
                            onChange={(e) =>
                              updateMachineRow(idx, {
                                capacity_by_length: e.target.value
                              })
                            }
                            style={{
                              ...compactInput,
                              height: 28
                            }}
                            min={0}
                            placeholder="0"
                            disabled={isDraftView || !isEditMode}
                          />
                        </td>

                        <td style={{ padding: "6px 8px", border: "1px solid #e5e7eb" }}>
                          <input
                            type="text"
                            value={row.available_capacity ? `${row.available_capacity} h` : "—"}
                            readOnly
                            style={{
                              ...compactInput,
                              height: 28,
                              background: "#f0fdf4",
                              fontWeight: 600,
                              color: "#166534"
                            }}
                          />
                        </td>

                        <td style={{ padding: "6px 8px", border: "1px solid #e5e7eb" }}>
                          <input
                            type="date"
                            value={row.breakdown_start_date || ""}
                            onChange={(e) =>
                              updateMachineRow(idx, {
                                breakdown_start_date: e.target.value
                              })
                            }
                            style={{ ...compactInput, height: 28 }}
                            disabled={isDraftView || !isEditMode}
                          />
                        </td>

                        <td style={{ padding: "6px 8px", border: "1px solid #e5e7eb" }}>
                          <input
                            type="date"
                            value={row.breakdown_end_date || ""}
                            onChange={(e) =>
                              updateMachineRow(idx, {
                                breakdown_end_date: e.target.value
                              })
                            }
                            style={{ ...compactInput, height: 28 }}
                            disabled={isDraftView || !isEditMode}
                          />
                        </td>

                        <td style={{ padding: "6px 8px", border: "1px solid #e5e7eb" }}>
                          <input
                            type="time"
                            value={row.breakdown_start_time || ""}
                            onChange={(e) =>
                              updateMachineRow(idx, {
                                breakdown_start_time: e.target.value
                              })
                            }
                            style={{ ...compactInput, height: 28 }}
                            disabled={isDraftView || !isEditMode}
                          />
                        </td>

                        <td style={{ padding: "6px 8px", border: "1px solid #e5e7eb" }}>
                          <input
                            type="time"
                            value={row.breakdown_end_time || ""}
                            onChange={(e) =>
                              updateMachineRow(idx, {
                                breakdown_end_time: e.target.value
                              })
                            }
                            style={{ ...compactInput, height: 28 }}
                            disabled={isDraftView || !isEditMode}
                          />
                        </td>

                      </tr>

                    ))}

                  </tbody>

                  {machineRows.length > 1 && (

                    <tfoot>

                      <tr
                        style={{
                          background: "#f1f5f9",
                          fontWeight: 700
                        }}
                      >

                        <td
                          colSpan={9}
                          style={{
                            padding: "10px 8px",
                            border: "1px solid #d1d5db",
                            textAlign: "right",
                            fontSize: 12,
                            color: "#1e293b"
                          }}
                        >
                          Totals →
                        </td>

                        <td
                          style={{
                            padding: "10px 8px",
                            border: "1px solid #d1d5db",
                            fontSize: 12,
                            color: "#1e293b"
                          }}
                        >
                          {machineRows
                            .reduce(
                              (sum, r) =>
                                sum +
                                (parseFloat(r.capacity_by_length) || 0),
                              0
                            )
                            .toFixed(2)}
                        </td>

                        <td
                          style={{
                            padding: "10px 8px",
                            border: "1px solid #d1d5db",
                            fontSize: 12,
                            color: "#166534"
                          }}
                        >
                          {machineRows
                            .reduce(
                              (sum, r) =>
                                sum +
                                (parseFloat(r.available_capacity) || 0),
                              0
                            )
                            .toFixed(2)} h
                        </td>

                        <td colSpan={4} style={{ padding: "10px 8px", border: "1px solid #d1d5db" }}></td>

                      </tr>

                    </tfoot>

                  )}

                </table>

            </div>
          </TableGuard>
        )}

                      <FieldBox st={fs("no_of_machines_breakdown")} label="No. of Machines Breakdown">
                        <input
                          type="number"
                          value={form.no_of_machines_breakdown}
                          onChange={(e) => updateForm({ no_of_machines_breakdown: e.target.value })}
                          style={compactInput}
                          disabled={isDraftView || !isEditMode}
                        />
                      </FieldBox>

                      <FieldBox st={fs("relevant_for_scheduling")} label="Relevant for Scheduling">
                        <select
                          value={form.relevant_for_scheduling}
                          onChange={(e) => updateForm({ relevant_for_scheduling: e.target.value })}
                          style={compactInput}
                          disabled={isDraftView || !isEditMode}
                        >
                          <option value="no">No</option>
                          <option value="yes">Yes</option>
                        </select>
                      </FieldBox>

                    </div>
                  </>
                )}

                {activeTab === 3 && (
                  <>
                    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
                      <FieldBox st={fs("start_date")} label="Start Date">
                        <input
                          type="date"
                          value={form.start_date}
                          onChange={(e) => updateForm({ start_date: e.target.value })}
                          style={compactInput}
                          disabled={isDraftView || !isEditMode}
                        />
                      </FieldBox>

                      <FieldBox st={fs("end_date")} label="End Date">
                        <input
                          type="date"
                          value={form.end_date}
                          onChange={(e) => updateForm({ end_date: e.target.value })}
                          style={compactInput}
                          disabled={isDraftView || !isEditMode}
                        />
                      </FieldBox>

                      <FieldBox st={fs("controlling_area")} label="Controlling Area">
                        <select
                          value={form.controlling_area}
                          onChange={(e) => updateForm({ controlling_area: e.target.value })}
                          style={compactInput}
                          disabled={isDraftView || !isEditMode}
                        >
                          <option value="">-- Select --</option>
                          {controllingAreaList.map((item) => (
                            <option key={item.id} value={item.name}>
                              {item.name}
                            </option>
                          ))}
                        </select>
                      </FieldBox>

                      <FieldBox st={fs("cost_center")} label="Cost Center">
                        <select
                          value={form.cost_center}
                          onChange={(e) => updateForm({ cost_center: e.target.value })}
                          style={compactInput}
                          disabled={isDraftView || !isEditMode}
                        >
                          <option value="">-- Select --</option>
                          {costCenterList.map((item) => (
                            <option key={item.id} value={item.name}>
                              {item.name}
                            </option>
                          ))}
                        </select>
                      </FieldBox>

                      {/* --------------------------- ACTIVITY TYPES (FLAT TABLE) ----------------------------- */}
                      <TableGuard st={fs("activities")} style={{ gridColumn: "1 / -1", marginTop: 10 }}>
                        <h4 style={{ margin: "10px 0 6px 0", fontSize: "13px", fontWeight: 600, color: '#1e293b' }}>Activity Types</h4>
                        <table style={styles.erpTable}>
                          <thead>
                            <tr>
                              <th style={{ ...styles.erpTh, width: "20%", textAlign: "center" }}>Select</th>
                              <th style={{ ...styles.erpTh, width: "40%" }}>Activity Type</th>
                              <th style={{ ...styles.erpTh, width: "40%" }}>Activity UOM</th>
                            </tr>
                          </thead>
                          <tbody>

                            {activityRows.map((row, idx) => (

                              <tr key={idx}>

                                {/* RADIO BUTTON */}
                                <td style={{ ...styles.erpTd, textAlign: 'center' }}>
                                  <input
                                    type="checkbox"
                                    name="activityType"

                                    checked={!!row.is_selected}
                                    disabled={isDraftView || !isEditMode}

                                    onChange={(e) => {

                                      const updated = [...activityRows];

                                      updated[idx].is_selected =
                                        e.target.checked ? 1 : null;

                                      setActivityRows(updated);

                                    }}
                                  />
                                </td>

                                {/* ACTIVITY TYPE */}
                                <td style={styles.erpTd}>
                                  {row.workcenteractivity}
                                </td>

                                {/* UOM */}
                                <td style={styles.erpTd}>
                                  <select
                                    value={
                                      row.workcenteractivityuom || ""
                                    }

                                    onChange={(e) => {

                                      const updated = [...activityRows];

                                      updated[idx]
                                        .workcenteractivityuom =
                                        e.target.value;

                                      setActivityRows(updated);

                                    }}

                                    className="form-select"
                                    style={compactInput}
                                    disabled={isDraftView || !isEditMode}
                                  >

                                    <option value="">
                                      -- Select UOM --
                                    </option>

                                    {uomList.map((uom) => (

                                      <option
                                        key={uom.id}
                                        value={uom.name}
                                      >
                                        {uom.name}
                                      </option>

                                    ))}

                                  </select>
                                </td>

                              </tr>

                            ))}

                          </tbody>
                        </table>

                      </TableGuard>
                    </div>
                  </>
                )}
              </div>

              {activeTab === 4 && (
                <div
                  style={{
                    background: "#fff",
                    borderRadius: 8,
                    padding: 10,
                    boxShadow: "0 1px 4px rgba(0,0,0,0.08)"
                  }}
                >

                  <table
                    style={{
                      width: "100%",
                      borderCollapse: "collapse"
                    }}
                  >

                    <thead>
                      <tr style={{ background: "#f8fafc" }}>
                        <th style={thStyle}>Date & Time</th>
                        <th style={thStyle}>User</th>
                        <th style={thStyle}>Action</th>
                        <th style={thStyle}>Status</th>
                        <th style={thStyle}>Field</th>
                        <th style={thStyle}>Old Value</th>
                        <th style={thStyle}>New Value</th>
                        <th style={thStyle}>Comment</th>
                      </tr>
                    </thead>

                    <tbody>

                      {workcenterLogs.length > 0 ? (
                        workcenterLogs.map((log) => (

                          <tr key={log.id}>

                            <td style={tdStyle}>
                              {formatDateDMY(log.created_at, true)}
                            </td>

                            <td style={tdStyle}>
                              {log.created_by || "-"}
                            </td>

                            <td style={tdStyle}>
                              {log.action_type || "-"}
                            </td>

                            <td style={tdStyle}>
                              {log.doc_status || "-"}
                            </td>

                            <td style={tdStyle}>
                              {log.field_name || "-"}
                            </td>

                            <td style={tdStyle}>
                              {log.old_value || "-"}
                            </td>

                            <td style={{
                              ...tdStyle,
                              fontWeight: 700
                            }}>
                              {log.new_value || "-"}
                            </td>

                            <td style={tdStyle}>
                              {log.comment || "-"}
                            </td>

                          </tr>
                        ))
                      ) : (
                        <tr>
                          <td
                            colSpan="8"
                            style={{
                              padding: 20,
                              textAlign: "center",
                              color: "#777"
                            }}
                          >
                            No change logs available
                          </td>
                        </tr>
                      )}

                    </tbody>
                  </table>
                </div>
              )}

              </div>

              <div style={{ padding: "12px 20px", borderTop: "1px solid #ddd", display: "flex", justifyContent: "flex-end", gap: 10 }}>
                <button
                  onClick={closeModal}
                  className="btn btn-outline-secondary btn-sm"
                  style={{
                    padding: "6px 14px",
                    fontSize: 13,
                  }}
                >
                  {isDraftView ? 'Close Preview' : 'Cancel'}
                </button>

                {!isDraftView && (
                  <>
                    {!editingId ? (

                      <button
                        onClick={handleSave}
                        disabled={saving}
                        className="btn btn-dark"
                        style={{
                          padding: "6px 14px",
                          fontSize: 13,
                        }}
                      >
                        {saving ? "Saving..." : "Save"}
                      </button>

                    ) : !isEditMode ? (

                      <button
                        disabled={!canEdit}
                        onClick={() => setIsEditMode(true)}
                        className="btn btn-dark"
                        style={{
                          padding: "6px 14px",
                          fontSize: 13,
                        }}
                      >
                        Edit
                      </button>

                    ) : (

                      <button
                        onClick={handleSave}
                        disabled={saving}
                        className="btn btn-dark"
                        style={{
                          padding: "6px 14px",
                          fontSize: 13,
                        }}
                      >
                        {saving ? "Updating..." : "Save"}
                      </button>

                    )}
                  </>
                )}
              </div>
            </div>
          </>
        )}

        <div style={{ position: 'fixed', bottom: 30, right: 30, display: 'flex', flexDirection: 'column', gap: 15, zIndex: 9999 }}>
          {draftsList.length > 0 && (
            <div onClick={() => setShowDraftModal(true)} style={{ background: '#1e293b', color: '#fff', padding: '12px 20px', borderRadius: 50, cursor: 'pointer', boxShadow: '0 8px 20px rgba(0,0,0,0.3)', display: 'flex', alignItems: 'center', gap: 10 }}>
              <span style={{ fontWeight: 'bold', fontSize: 15 }}>📂 Pending Drafts</span>
              <span style={{ background: '#ef4444', color: 'white', padding: '2px 8px', borderRadius: 20, fontSize: 12, fontWeight: 'bold' }}>{draftsList.length}</span>
            </div>
          )}
        </div>


        {showUploadModal && (
          <div style={{ position: "fixed", top: 0, left: 0, width: "100vw", height: "100vh", background: "rgba(0,0,0,0.5)", zIndex: 1060, display: "flex", justifyContent: "center", alignItems: "center" }}>
            <div style={{ background: "white", width: "400px", borderRadius: 12, padding: 25, boxShadow: "0 10px 30px rgba(0,0,0,0.2)" }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20 }}>
                <h4 style={{ margin: 0, fontSize: "18px", color: "#1e293b" }}>Upload Work Centers</h4>
                <button onClick={() => setShowUploadModal(false)} style={{ background: "transparent", border: "none", fontSize: 18, cursor: "pointer", color: "#64748b" }}>✖</button>
              </div>
              <div style={{ border: "2px dashed #cbd5e1", borderRadius: 8, padding: 30, textAlign: "center", background: "#f8fafc" }}>
                <p style={{ margin: "0 0 15px 0", fontSize: 14, color: "#475569" }}>Upload your Work Center Template</p>
                <input type="file" id="workcenterExcelUpload" accept=".xlsx, .xls, .csv" style={{ display: "none" }} onChange={handleBulkExcelUpload} />
                <label htmlFor="workcenterExcelUpload" style={{ background: "#021843", color: "white", padding: "8px 20px", borderRadius: 6, cursor: "pointer", fontWeight: 600, display: "inline-block", marginBottom: 15 }}>Browse File</label>
                <div>
                  <a href="/assets/files/workcenter.xlsx" download style={{ fontSize: 13, color: "#021843", textDecoration: "none", fontWeight: 500 }}>⬇ Download Template</a>
                </div>
              </div>
            </div>
          </div>
        )}

        {showDraftModal && (
          <div style={{ position: "fixed", top: 0, left: 0, width: "100vw", height: "100vh", background: "rgba(0,0,0,0.5)", zIndex: 1050, display: "flex", justifyContent: "center", alignItems: "center" }}>
            <div style={{ background: "white", width: "85vw", height: "85vh", borderRadius: 12, padding: 20, display: "flex", flexDirection: "column", boxShadow: "0 10px 30px rgba(0,0,0,0.2)" }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", borderBottom: "1px solid #eee", paddingBottom: 15, marginBottom: 15 }}>
                <h3 style={{ margin: 0, color: "#1e293b" }}>👀 Preview Uploaded Work Centers</h3>
                <button onClick={() => setShowDraftModal(false)} style={{ background: "#f1f5f9", border: "none", padding: "8px 16px", borderRadius: 6, cursor: "pointer", fontWeight: 600 }}>Close</button>
              </div>
              <div style={{ flex: 1, overflowY: "auto", paddingRight: 10 }}>
                {draftsList.length === 0 ? (
                  <p style={{ textAlign: 'center', marginTop: 40, color: '#666' }}>No Pending Work Centers found.</p>
                ) : (
                  <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "13px" }}>
                    <thead>
                      <tr style={{ background: "#f1f1f1" }}>
                        <th style={{ padding: 8, borderBottom: "1px solid #ccc", textAlign: "left" }}>Workcenter Name</th>
                        <th style={{ padding: 8, borderBottom: "1px solid #ccc", textAlign: "left" }}>Category</th>
                        <th style={{ padding: 8, borderBottom: "1px solid #ccc", textAlign: "left" }}>Location</th>
                        <th style={{ padding: 8, borderBottom: "1px solid #ccc", textAlign: "left" }}>Cost Center</th>
                        <th style={{ padding: 8, borderBottom: "1px solid #ccc", textAlign: "left" }}>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {draftsList.map((draft) => (
                        <tr key={draft.workcenter_id}>
                          <td style={{ padding: 8, borderBottom: "1px solid #eee" }}><strong>{draft.workcenter_name}</strong></td>
                          <td style={{ padding: 8, borderBottom: "1px solid #eee" }}>{draft.workcenter_category || "-"}</td>
                          <td style={{ padding: 8, borderBottom: "1px solid #eee" }}>{draft.location_name || "-"}</td>
                          <td style={{ padding: 8, borderBottom: "1px solid #eee" }}>{draft.cost_center || "-"}</td>
                          <td style={{ padding: 8, borderBottom: "1px solid #eee" }}>
                            <button onClick={() => openDraftView(draft)} style={{ padding: "6px 10px", background: "#1E88E5", color: "white", border: "none", borderRadius: 4, cursor: "pointer", fontSize: 12, marginRight: 8 }}>View</button>
                            <button onClick={() => handlePublishDraft(draft.workcenter_name)} disabled={publishingId === draft.workcenter_name || isPublishingAll} style={{ padding: "6px 10px", background: (publishingId === draft.workcenter_name || isPublishingAll) ? "#6c757d" : "#10b981", color: "white", border: "none", borderRadius: 4, cursor: "pointer", fontSize: 12 }}>
                              {publishingId === draft.workcenter_name ? "Saving..." : "Save"}
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
              {draftsList.length > 0 && (
                <div style={{ borderTop: "1px solid #eee", paddingTop: 15, marginTop: 15, display: "flex", justifyContent: "flex-end" }}>
                  <button onClick={handlePublishAllDrafts} disabled={isPublishingAll} style={{ background: isPublishingAll ? "#6c757d" : "#10b981", color: "white", border: "none", padding: "10px 24px", borderRadius: 6, cursor: isPublishingAll ? "not-allowed" : "pointer", fontWeight: 600, fontSize: 15 }}>
                    {isPublishingAll ? "Saving All..." : "Save All"}
                  </button>
                </div>
              )}
            </div>
          </div>
        )}

        {deleteTarget && (
          <div
            style={{
              position: "fixed",
              top: 0,
              left: 0,
              width: "100vw",
              height: "100vh",
              background: "rgba(0,0,0,0.5)",
              zIndex: 1070,
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
            }}
            onClick={closeDeleteConfirm}
          >
            <div
              style={{
                background: "white",
                width: "380px",
                maxWidth: "90vw",
                borderRadius: 12,
                padding: "28px 25px 25px",
                boxShadow: "0 10px 30px rgba(0,0,0,0.2)",
                position: "relative",
                textAlign: "center",
              }}
              onClick={(e) => e.stopPropagation()}
            >
              <button
                onClick={closeDeleteConfirm}
                style={{
                  position: "absolute",
                  top: 14,
                  right: 16,
                  background: "transparent",
                  border: "none",
                  fontSize: 20,
                  cursor: "pointer",
                  color: "#334155",
                  lineHeight: 1,
                }}
              >
                ✕
              </button>

              <div
                style={{
                  width: 56,
                  height: 56,
                  borderRadius: "50%",
                  background: "#ef4444",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  margin: "0 auto 16px",
                }}
              >
                <svg
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="#ffffff"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <polyline points="3 6 5 6 21 6" />
                  <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
                  <path d="M10 11v6" />
                  <path d="M14 11v6" />
                  <path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2" />
                </svg>
              </div>

              <h4 style={{ margin: "0 0 8px", fontSize: 19, color: "#0f172a" }}>
                Delete Work Center
              </h4>

              <p style={{ margin: "0 0 22px", fontSize: 14, color: "#64748b" }}>
                Delete workcenter "{deleteTarget.workcenter_name}"? This will
                hide it from the list.
              </p>

              <button
                onClick={confirmDeleteWorkcenter}
                disabled={isDeleting}
                style={{
                  width: "100%",
                  background: "#ef4444",
                  color: "white",
                  border: "none",
                  padding: "12px",
                  borderRadius: 8,
                  fontWeight: 600,
                  fontSize: 15,
                  cursor: isDeleting ? "not-allowed" : "pointer",
                  marginBottom: 12,
                  opacity: isDeleting ? 0.7 : 1,
                }}
              >
                {isDeleting ? "Deleting..." : "Yes, Delete Work Center"}
              </button>

              <button
                onClick={closeDeleteConfirm}
                disabled={isDeleting}
                style={{
                  width: "100%",
                  background: "transparent",
                  border: "none",
                  color: "#334155",
                  fontSize: 14,
                  fontWeight: 500,
                  cursor: isDeleting ? "not-allowed" : "pointer",
                  padding: "6px",
                }}
              >
                Cancel
              </button>
            </div>
          </div>
        )}

      </div>
    </Layout>
  );
};

export default Workcenter;